// GDMCOMMENT: Latitudes and longitudes used in this file are either sourced from GrabPlaces, GrabMaps, OSM or randomly created by the developer and are not obtained from other external sources.
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import { Form, Modal } from 'grab-ui';
import { connect } from 'react-redux';
import _get from 'lodash/get';

import {
  trackPageView,
  trackClick,
  event,
  cat,
  custom,
} from 'common/utils/instrumentation';
import { getCompany, getCompanyID, getUserCountryCode } from 'helper/user';
import AddressField from 'components/v2/address-field/address-field.jsx';
import Map from 'components/booking/map/map';
import InputField from 'components/form-field/input-field/input-field';
import { cleanAllAction } from 'actions/booking-actions';
import { FIELD_LABEL } from 'constants/booking-constants';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_BULK_UPLOAD_EDIT_ADDRESS_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';
import ScribeDuck from 'ducks/scribe-duck.js';

import css from './select-location-modal.less';

const ADDRESS_DETAIL_FIELD_NAME = 'selectPOIAddressDetailV2';

class SelectLocationModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      coordinates: {},
    };
  }

  componentDidMount() {
    if (_get(this.props, 'addressEntity.coordinates')) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        coordinates: _get(this.props, 'addressEntity.coordinates'),
      });
    }
  }

  componentDidUpdate(prevProps, _prevState) {
    const {
      visible,
      addressEntity,
      cleanAll,
      userInfo,
      form: { setFieldsValue },
    } = this.props;
    const { visible: prevVisible } = prevProps;

    if (visible && visible !== prevVisible) {
      trackPageView(
        event.BULK_UPLOAD_EDIT_ADDRESS_PV,
        cat.GE_BULK_UPLOAD_EDIT_ADDRESS,
        {
          [custom.COMPANY_ID]: getCompanyID(userInfo),
          POI_ID: addressEntity.poiID,
        },
      );
      // setTimeout to make sure field has been set after rendering
      setTimeout(() => {
        setFieldsValue({
          [ADDRESS_DETAIL_FIELD_NAME]: addressEntity.instruction,
        });
      }, 700);
    }

    if (!visible && prevVisible) {
      cleanAll();
    }

    if (
      JSON.stringify(_get(this.props, 'addressEntity.coordinates', {})) !==
      JSON.stringify(_get(prevProps, 'addressEntity.coordinates', {}))
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        coordinates: _get(this.props, 'addressEntity.coordinates', {}),
      });
    }
  }

  componentWillUnmount() {
    const { cleanAll } = this.props;

    cleanAll();
  }

  onUpdateAddress = () => {
    const { addressEntity } = this.state;
    const { originalAddressEntity } = this.props;
    const {
      onClickUpdateRepickLocation,
      form: { getFieldValue },
      userInfo,
      isSender,
    } = this.props;
    const newAddressEntity = {
      ...addressEntity,
      instruction: getFieldValue(ADDRESS_DETAIL_FIELD_NAME),
    };

    trackClick(
      event.BULK_UPLOAD_EDIT_ADDRESS_PV,
      cat.GE_BULK_UPLOAD_EDIT_ADDRESS,
      'CONFIRM',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
        POI_ID: newAddressEntity.poiID,
      },
    );
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_BULK_UPLOAD_EDIT_ADDRESS_WEB,
      eventName: SCRIBE_BULK_UPLOAD_EDIT_ADDRESS_EVENTS.CONFIRM,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.STEP_TYPE]: isSender ? 'PICKUP' : 'DROPOFF',
        [isSender
          ? SCRIBE_EVENT_PARAMS.PICKUP_POI_ID
          : SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: newAddressEntity.poiID,
        [SCRIBE_EVENT_PARAMS.BULK_EDIT_ORIGINAL]: _get(
          originalAddressEntity,
          'address',
        ),
        [SCRIBE_EVENT_PARAMS.BULK_EDIT_ADJUSTED]: _get(
          newAddressEntity,
          'address',
        ),
      },
    });

    onClickUpdateRepickLocation(newAddressEntity);
  };

  onSelectAddress = (_, addressEntity) => {
    const {
      userInfo,
      scribeExpressBulkUploadEditAddressWebAddress,
    } = this.props;
    trackClick(
      event.BULK_UPLOAD_EDIT_ADDRESS_PV,
      cat.GE_BULK_UPLOAD_EDIT_ADDRESS,
      'ADDRESS',
      {
        [custom.COMPANY_ID]: getCompanyID(userInfo),
      },
    );
    const { latlng } = addressEntity;
    // convert V2 address field entity object to bulk upload acceptable address entity
    const convertedAddressEntity = {
      address: _get(addressEntity, 'address.combined_address'),
      keywords: _get(addressEntity, 'address.name'),
      countryID: _get(addressEntity, 'address.country_id'),
      cityID: _get(addressEntity, 'address.cityId'),
      poiID: _get(addressEntity, 'id'),
      coordinates: latlng,
      isReverse: false,
    };
    this.setState({
      coordinates: latlng,
      addressEntity: convertedAddressEntity,
    });
    scribeExpressBulkUploadEditAddressWebAddress(
      _get(convertedAddressEntity, 'address'),
    );
  };

  render() {
    const { coordinates } = this.state;
    const {
      addressEntity,
      visible,
      onClickCancelRepickLocation,
      form,
      userInfo,
      isSender,
      countryCode: propsCountryCode,
    } = this.props;
    const countryCode = propsCountryCode || getUserCountryCode(userInfo);
    const initAddressCoordinates =
      coordinates && coordinates.latitude && coordinates.longitude
        ? `${coordinates.latitude}, ${coordinates.longitude}`
        : '';

    return (
      <Modal
        centered
        visible={visible}
        onOk={this.onUpdateAddress}
        onCancel={onClickCancelRepickLocation}
        className={css.selectLocationModal}
        okText={I18n.t('v2.commonButton.confirm')}
        cancelText={I18n.t('v2.commonButton.cancel')}
        closable={false}
        width="50%"
      >
        <div className={css.introduce}>
          <h5>
            {I18n.t('v2.homepage.uploadOrder.reviewTable.action.editAddress')}
          </h5>
          <h6>{`${I18n.t(
            'v2.homepage.uploadOrder.reviewTable.action.originalAddress',
          )}: ${_get(addressEntity, 'address', '')}`}</h6>
        </div>
        <div className={css.mapView}>
          <Map
            id="selectLocationMap"
            latlng={coordinates}
            countryCode={countryCode}
            isSender={isSender}
          />
        </div>
        <div className={css.inputs}>
          <AddressField
            index={1}
            initialValue={initAddressCoordinates}
            onSelectAddress={this.onSelectAddress}
            enableDelete={false}
            city={_get(userInfo, 'selectedCity', {})}
            company={getCompany(userInfo)}
            isSender={isSender}
          />
          <InputField
            id={ADDRESS_DETAIL_FIELD_NAME}
            placeholder={I18n.t(FIELD_LABEL.LOCATION_DETAILS)}
            form={form}
          />
        </div>
      </Modal>
    );
  }
}

const mapDispatchToProps = dispatch => ({
  cleanAll: () => dispatch(cleanAllAction()),
  scribeExpressBulkUploadEditAddressWebAddress: adjustedToAddress =>
    dispatch(
      ScribeDuck.creators.scribeExpressBulkUploadEditAddressWebAddress(
        adjustedToAddress,
      ),
    ),
});

SelectLocationModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  userInfo: PropTypes.shape({}),
  addressEntity: PropTypes.shape({}),
  originalAddressEntity: PropTypes.shape({}),
  onClickCancelRepickLocation: PropTypes.func.isRequired,
  onClickUpdateRepickLocation: PropTypes.func.isRequired,
  cleanAll: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  isSender: PropTypes.bool,
  countryCode: PropTypes.string,
  scribeExpressBulkUploadEditAddressWebAddress: PropTypes.func.isRequired,
};

SelectLocationModal.defaultProps = {
  userInfo: {},
  addressEntity: {},
  originalAddressEntity: {},
  isSender: false,
  countryCode: null,
};

export default connect(null, mapDispatchToProps)(
  Form.create()(SelectLocationModal),
);
